import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { Box } from '../../shared_components/UI/Box'
import { H1, H2, P } from '../../shared_components/Library/Typeography'
import {
  defaultTransition,
  theme,
  useShouldShowForThreshold,
} from '../../theme'
import { ThemedButton } from '../../shared_components/ThemedButton'
import styled, { css } from 'styled-components'
import { Link } from 'react-router-dom'
import AnimatedLoader from '../../shared_components/AnimatedLoader'
import { boxGapMarginsCssRules } from '../../shared_components/Header/styles'
import { AppType } from '../../shared_components/Library/AppToggler'
import { useSwipeable } from 'react-swipeable'
import { CarouselIndicator, fadeSlideInLeft, fadeSlideInRight } from '.'
import routes from '../../router/routes'

export const SLIDES = ['staking', 'launch', 'drops', 'labs']
export type SlidesType = typeof SLIDES[number]
export function HomeHero({
  titleIcon,
  smallTitle,
  yellowTitle,
  title,
  description,
  buttonText,
  buttonLink,
  tertiaryButtonText,
  tertiaryButtonLink,
  shouldTertiaryButtonLookLikePrimary = false,
  heroLeft,
  heroRight,
  sliderEnabled,
  onSwipeLeft,
  onSwipeRight,
  slidesCount = 0,
  getIsActiveSlide,
  onIndicatorClick,
  invertColors = false,
}: {
  titleIcon?: React.ReactNode
  smallTitle?: string | null
  yellowTitle?: string | null
  title?: string
  description?: string
  buttonText?: string
  buttonLink?: string
  tertiaryButtonText?: string
  tertiaryButtonLink?: string
  shouldTertiaryButtonLookLikePrimary?: boolean
  heroLeft?: React.ReactNode
  heroRight?: React.ReactNode
  sliderEnabled?: boolean
  onSwipeLeft?: () => void
  onSwipeRight?: () => void
  slidesCount?: number
  getIsActiveSlide?: (currentIndex: number) => boolean
  onIndicatorClick?: (currentIndex: number) => void
  invertColors?: boolean
}) {
  const isAtLeastMediumDisplaySize = useShouldShowForThreshold('medium')

  const [animationDirection, setAnimationDirection] = useState<
    'left' | 'right'
  >('right')
  const [isAnimating, setIsAnimating] = useState(false)

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (!onSwipeLeft || !sliderEnabled) return
      onSwipeLeft()
      setAnimationDirection('right')
      triggerAnimation()
    },
    onSwipedRight: () => {
      if (!onSwipeRight || !sliderEnabled) return
      onSwipeRight()
      setAnimationDirection('left')
      triggerAnimation()
    },
  })

  const triggerAnimation = () => {
    setIsAnimating(true)
    setTimeout(() => {
      setIsAnimating(false)
    }, 1000) // Match this duration with the animation duration
  }

  return (
    <HeroCont
      padT="section"
      jsb
      ac={isAtLeastMediumDisplaySize}
      dc={!isAtLeastMediumDisplaySize}
      {...(sliderEnabled && {
        animation:
          animationDirection === 'right' ? fadeSlideInRight : fadeSlideInLeft,
        isAnimating: isAnimating,
      })}
      {...swipeHandlers}
    >
      {heroLeft}
      <Box dc mw="516px" g="var(--units-32, 32px)">
        <Box dc fw g="var(--Spacing-Padding-L, 20px)">
          {titleIcon}
          {!!smallTitle && (
            <H2 c={invertColors ? 'text-invert' : 'text-brandPrimary'} s="S">
              {smallTitle}
            </H2>
          )}
          {!!yellowTitle && (
            <H1 c={invertColors ? 'text-invert' : 'text-brandPrimary'} uc>
              {yellowTitle}
            </H1>
          )}
          <H1 s="3XL" uc twb c={invertColors ? 'text-invert' : 'text-primary'}>
            {title}
          </H1>
          <P s="XL" c={invertColors ? 'text-invert' : 'text-primary'}>
            {description}
          </P>
          {(!!buttonLink || !!tertiaryButtonLink) && (
            <Box g={10}>
              <Link to={buttonLink}>
                <ThemedButton
                  $styleType={invertColors ? 'primary-invert' : 'primary'}
                  w="auto"
                >
                  {buttonText}
                </ThemedButton>
              </Link>
              {tertiaryButtonText && tertiaryButtonLink && (
                <Link to={tertiaryButtonLink}>
                  <ThemedButton
                    $styleType={
                      shouldTertiaryButtonLookLikePrimary
                        ? invertColors
                          ? 'primary-invert'
                          : 'primary'
                        : 'tertiary'
                    }
                    w="auto"
                  >
                    {tertiaryButtonText}
                  </ThemedButton>
                </Link>
              )}
            </Box>
          )}
        </Box>
        {sliderEnabled && (
          <Box>
            {Array(slidesCount)
              .fill(null)
              .map((_, index) => {
                const isActive = getIsActiveSlide(index)
                return (
                  <CarouselIndicator
                    key={index}
                    isActive={isActive}
                    onClick={() => onIndicatorClick(index)}
                    invertColors={invertColors}
                  />
                )
              })}
          </Box>
        )}
      </Box>
      {heroRight}
    </HeroCont>
  )
}

export function getHomeSliderProps({ currentSlide, tertiary, invertColors }) {
  const titleIcon =
    currentSlide === 'staking' ? undefined : (
      <img
        src={`/img/logos/app-logos/${currentSlide}.svg`}
        alt="ZAP Logo"
        style={{ filter: invertColors ? 'saturate(0) invert(1)' : 'none' }}
      />
    )

  const appToYellowTitle = {
    staking: '',
    launch: '',
    drops: '',
    labs: '',
  }
  const appToTitle = {
    staking: 'Staking is here',
    launch: 'Discover the next big token',
    drops: 'Earn airdrop rewards',
    labs: 'Early Access to vetted projects',
  }
  const appToDescription = {
    staking: 'Explore the benefits of staking on ZAP',
    launch:
      'No pre-sale, no insiders, fully audited smart contracts, buy and sell at any time.  ZAP Launch is the safest and simplest way to degen.',
    drops:
      'For the community people. Drops is a permissionless, mission-based airdrop platform that rewards you for your contributions. ',
    labs: 'The simplest, safest and fairest way to invest in, and launch trending crypto tokens. Get started with less than a dollar. ',
  }
  const appToButtonText = {
    staking: 'Stake & claim Vaults',
    launch: 'View trending tokens',
    drops: 'View all airdrops',
    labs: 'View all projects',
  }
  const appToButtonLink = {
    staking: routes.staking.vaults,
    launch: routes.memecoins.index,
    drops: routes.drops.index,
    labs: routes.labs.index,
  }

  const tertiaryButton = {
    staking: {
      text: 'Stake tokens',
      link: routes.staking.tokens,
      shouldLookLikePrimary: true,
    },
    launch: {
      text: 'Launch a token',
      link: routes.memecoins.launcher,
      shouldLookLikePrimary: false,
    },
  }

  const _yellowTitle = appToYellowTitle[currentSlide]
  const _title = appToTitle[currentSlide]
  const _description = appToDescription[currentSlide]
  const _buttonText = appToButtonText[currentSlide]
  const _buttonLink = appToButtonLink[currentSlide]

  return {
    slidesCount: 4,
    titleIcon,
    yellowTitle: _yellowTitle,
    title: _title,
    description: _description,
    buttonText: _buttonText,
    buttonLink: _buttonLink,
    tertiaryButtonText: tertiary ? tertiaryButton[currentSlide]?.text : null,
    tertiaryButtonLink: tertiary ? tertiaryButton[currentSlide]?.link : null,
    shouldTertiaryButtonLookLikePrimary: tertiary
      ? tertiaryButton[currentSlide]?.shouldLookLikePrimary
      : false,
  }
}

export function HeroRight({
  currentSlide,
  invertColors = false,
}: {
  currentSlide: string
  invertColors?: boolean
}) {
  const [hasLoaded, setHasLoaded] = useState(false)
  const videoSrc = `/video/hero/${currentSlide}.webm`
  const imageSrc = `/img/hero/${currentSlide}.svg`

  const OnlyImageSupportedSlides = ['staking']

  useEffect(() => {
    setHasLoaded(false)
  }, [videoSrc])

  const VideoBlock = useCallback(() => {
    if (OnlyImageSupportedSlides.includes(currentSlide)) {
      return <Fragment />
    }

    return (
      <div
        style={{
          background: invertColors
            ? theme.semantics.zap.text.invert
            : 'transparent',
          borderRadius: '8px',
        }}
      >
        <HomeVideo
          autoPlay
          muted
          loop
          playsInline
          onLoadedData={() => {
            setHasLoaded(true)
          }}
        >
          <source src={videoSrc} type="video/webm" />
        </HomeVideo>
      </div>
    )
  }, [videoSrc])

  const ImageBlock = useCallback(() => {
    if (!OnlyImageSupportedSlides.includes(currentSlide)) {
      return <Fragment />
    }

    return (
      <div
        style={{
          width: '100%',
          background: invertColors
            ? theme.semantics.zap.text.invert
            : 'transparent',
          borderRadius: '8px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <HomeImage
          src={imageSrc}
          alt="ZAP Logo"
          onLoad={() => {
            setHasLoaded(true)
          }}
        />
      </div>
    )
  }, [imageSrc])

  return (
    <RightSectionContainer>
      {!hasLoaded && (
        <LoadingContainer c minW="50vw" minH="300px">
          <AnimatedLoader onlyWhite size={80} />
        </LoadingContainer>
      )}
      <VideoBlock />
      <ImageBlock />
    </RightSectionContainer>
  )
}
const HeroCont = styled(Box)<{ animation?: any; isAnimating?: boolean }>`
  ${boxGapMarginsCssRules}
  ${defaultTransition}

  ${({ animation, isAnimating }) =>
    animation &&
    isAnimating &&
    css`
      animation: ${animation} 1s ease-in-out;
    `}
`
const RightSectionContainer = styled(Box)`
  @media screen and (min-width: ${({ theme }) =>
      theme.breakpoints.expanded}px) {
    min-height: 500px;
  }
  @media screen and (min-width: ${({ theme }) =>
      theme.breakpoints.expandedPlus}px) {
    min-height: 690px;
    max-height: 690px;
  }
`
const LoadingContainer = styled(Box)`
  height: 100%;
  @media screen and (min-width: ${({ theme }) =>
      theme.breakpoints.expanded}px) {
    min-height: 500px;
  }
  @media screen and (min-width: ${({ theme }) =>
      theme.breakpoints.expandedPlus}px) {
    min-height: 690px;
    max-height: 690px;
  }
`

export const HomeVideo = styled.video`
  display: block;
  width: 100%;
  height: auto;
  max-height: 500px;
  @media screen and (min-width: ${({ theme }) =>
      theme.breakpoints.expandedPlus}px) {
    max-height: 520px;
  }
`

export const HomeImage = styled.img`
  width: 100%;
  height: 100%;
  max-height: 500px;

  @media screen and (min-width: ${({ theme }) =>
      theme.breakpoints.expandedPlus}px) {
    max-height: 520px;
  }
`

export function ProductsHero({
  invertColors,
  tertiary = true,
}: {
  invertColors?: boolean
  tertiary?: boolean
}) {
  const [currentSlide, setCurrentApp] = useState<AppType>('staking')

  const onSwipeLeft = useCallback(() => {
    const currentIndex = SLIDES.indexOf(currentSlide)
    const nextIndex = (currentIndex + 1) % SLIDES.length
    setCurrentApp(SLIDES[nextIndex])
  }, [currentSlide])
  const onSwipeRight = useCallback(() => {
    const currentIndex = SLIDES.indexOf(currentSlide)
    const prevIndex = (currentIndex - 1 + SLIDES.length) % SLIDES.length
    setCurrentApp(SLIDES[prevIndex])
  }, [currentSlide])
  const sliderProps = useMemo(
    () => getHomeSliderProps({ currentSlide, tertiary, invertColors }),
    [currentSlide, tertiary, invertColors],
  )

  const getIsActiveSlide = useCallback(
    (index: number) => {
      return SLIDES.indexOf(currentSlide) === index
    },
    [currentSlide],
  )

  const onIndicatorClick = useCallback(
    (index: number) => {
      setCurrentApp(SLIDES[index])
    },
    [currentSlide],
  )

  return (
    <HomeHero
      sliderEnabled
      invertColors={invertColors}
      onSwipeLeft={onSwipeLeft}
      onSwipeRight={onSwipeRight}
      getIsActiveSlide={getIsActiveSlide}
      onIndicatorClick={onIndicatorClick}
      {...sliderProps}
      heroRight={
        <HeroRight currentSlide={currentSlide} invertColors={invertColors} />
      }
    />
  )
}
